import { ArrowRight, PlusCircle } from "iconoir-react";
import { FormProvider } from "react-hook-form";
import { Button, useToastContext } from "@ttc3k/trekker";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Flex } from "styled-system/jsx";
import { AnimatedPage, Collapsible, WhereaboutsModal } from "shared/components";
import { useTranslation } from "react-i18next";
import {
  useAddOperatorForm,
  BusinessInfoScreen,
  BusinessLocationScreen,
  BusinessInviteScreen,
} from "apps/directory/Operator/components";
import { UserFindOneQuery } from "gql/generated.ts";
import i18n from "i18next";

type AddOperatorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationIds?: string[];
  user?: UserFindOneQuery["userFindOne"];
  skipInvite?: boolean;
};

export type AddOperatorModalScreen = "info" | "location" | "invite";

export const AddOperatorModal = ({
  isOpen,
  onClose,
  organizationIds,
  user,
  skipInvite,
}: AddOperatorModalProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const [screen, setScreen] = useState<AddOperatorModalScreen>("info");
  const { form, onSubmit, defaultValues } = useAddOperatorForm({
    organizationIds,
    user,
    onSuccess: (operatorName, invitedUser?) => {
      form.reset(defaultValues);
      setScreen("info");
      onClose();
      toastFactory.create({
        title: "Success!",
        description: `Operator ${operatorName} was created successfully${invitedUser ? ` and ${invitedUser} was invited` : ""}`,
      });
    },
    onError: (message) => {
      toastFactory.create({
        title: i18n.t("core:ERROR.TITLE"),
        description: message,
      });
    },
    onClose: onClose,
  });

  form.watch("invitedUser");
  const isInvitingUser = form.getFieldState("invitedUser").isDirty;
  const businessName = form.watch("businessName");

  const formId = "add-operator-form";

  const handleClose = () => {
    form.reset(defaultValues);
    setScreen("info");
    onClose();
  };

  const isOnboarding = screen === "location" && skipInvite;

  const handleReviewClick = async (nextScreen: AddOperatorModalScreen) => {
    const isValid = await form.trigger();

    const isInfoScreenValid =
      !form.getFieldState("businessName").invalid &&
      !form.getFieldState("email").invalid &&
      !form.getFieldState("website").invalid &&
      !form.getFieldState("phone").invalid &&
      !form.getFieldState("tollFreePhone").invalid &&
      !form.getFieldState("businessType").invalid;

    // If all inputs on the "info" screen are valid,
    // move onto the next screen "location" and clear form errors so that the
    // user doesn't see them upon the above form.trigger()
    if (isValid || (nextScreen === "location" && isInfoScreenValid)) {
      form.clearErrors();

      // Bug with react-hook-forms, when screen state changes at the same time
      // as the form is triggered, it triggers the submission of the form which
      // we don't want

      if (!(nextScreen === "invite" && isOnboarding)) {
        setTimeout(() => setScreen(nextScreen), 1);
      }
    }
  };

  const getCurrentScreen = (screen: AddOperatorModalScreen) => {
    switch (screen) {
      case "info":
        return <BusinessInfoScreen />;
      case "location":
        return <BusinessLocationScreen />;
      case "invite":
        return <BusinessInviteScreen form={form} />;
    }
  };

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("directory:OPERATOR.ADD_OPERATOR_MODAL.TITLE")}
      description={t("directory:OPERATOR.ADD_OPERATOR_MODAL.DESCRIPTION")}
      HeaderIcon={PlusCircle}
      cancelButtonProps={{ onClick: handleClose }}
      customFooter={
        <Flex
          px={"500"}
          py={"400"}
          gap={"150"}
          justifyContent={"space-between"}
        >
          {(screen === "location" || screen === "invite") && (
            <Button
              w={"full"}
              size={"lg"}
              mode={"light"}
              onClick={() =>
                setScreen(screen === "location" ? "info" : "location")
              }
            >
              Back
            </Button>
          )}
          <Button
            w={"full"}
            visual={"accent.blue"}
            size={"lg"}
            form={screen === "invite" || isOnboarding ? formId : undefined}
            type={screen === "invite" || isOnboarding ? "submit" : "button"}
            // disabled={mainProductID.length < 1}
            isLoading={form.formState.isSubmitting}
            Icon={screen === "info" ? ArrowRight : undefined}
            isIconRightAligned={screen === "info" ? true : undefined}
            onClick={
              screen === "info" || screen === "location"
                ? () =>
                    handleReviewClick(screen === "info" ? "location" : "invite")
                : undefined
            }
          >
            {screen === "invite" || isOnboarding
              ? `${t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.CREATE")}${isInvitingUser ? t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.INVITE") : ""}`
              : t("core:NEXT")}
          </Button>
        </Flex>
      }
    >
      <FormProvider {...form}>
        <form onSubmit={onSubmit} id={formId}>
          <Collapsible open={true} key={screen}>
            <AnimatePresence mode={"wait"}>
              <AnimatedPage
                key={screen}
                // Don't display the animation on initial load on the first screen
                // After the initial load, display all animations
                initial={
                  screen === "info" && businessName.length < 1
                    ? "center"
                    : screen === "info"
                      ? "exit"
                      : "enter"
                }
              >
                {getCurrentScreen(screen)}
              </AnimatedPage>
            </AnimatePresence>
          </Collapsible>
        </form>
      </FormProvider>
    </WhereaboutsModal>
  );
};
